/**
 * EBL / VRM overlay — the adjustable bearing line (EBL) and single range circle
 * (VRM) drawn from own ship. Deliberately styled to read DIFFERENTLY from the
 * range-rings overlay so the two are never confused: range rings are thin grey
 * concentric circles; the EBL/VRM pair is a bold accent colour, the EBL a solid
 * bearing line and the VRM a single dashed circle, each carrying its own label
 * (the bearing on the line, the range on the circle).
 *
 * The geometry is true-distance/true-bearing from the boat (rhumb walks from the
 * centre), recomputed as the boat moves. Pure rendering: MapView passes the live
 * centre and the user's EBL/VRM settings.
 */
import type { Map as MaplibreMap, GeoJSONSource } from 'maplibre-gl';
import type { Feature, FeatureCollection } from 'geojson';
import { rhumbDestination, rhumbCircle, toDisplayBearing, bearingSuffix } from '@/nav';
import { getPalette } from './style/palettes';
import type { Palette } from '@/store';

const SOURCE_ID = 'ebl-vrm';

/** Bottom -> top draw order: VRM circle, then the EBL line, then the labels on
 *  top. Used both when creating the layers and when re-seating them (below). */
const EBL_VRM_LAYER_IDS = ['ebl-vrm-vrm', 'ebl-vrm-pi', 'ebl-vrm-ebl', 'ebl-vrm-label'];

/** How far out the EBL bearing line is drawn (nm). Long enough to cross the view
 * at the scales this training area is used at, without being unbounded. */
const EBL_LENGTH_NM = 60;

/** The PI line runs this far EACH WAY from its offset origin (nm). */
const PI_LENGTH_NM = 30;

export interface EblVrmOpts {
  centre: [number, number] | null;
  eblOn: boolean;
  eblBearingDeg: number;
  vrmOn: boolean;
  vrmRangeNm: number;
  piOn: boolean;
  piBearingDeg: number;
  /** Signed perpendicular offset, nm. Positive = starboard of the bearing. */
  piOffsetNm: number;
}

/** A bearing label in the current display reference, e.g. "090°T" or "088°M".
 *  Input is a TRUE bearing; conversion + suffix follow the global setting. */
function bearingLabel(deg: number): string {
  return `${String(Math.round(toDisplayBearing(deg))).padStart(3, '0')}${bearingSuffix()}`;
}
/** A range label, e.g. "1.50 nm". */
function rangeLabel(nm: number): string {
  return `${nm.toFixed(nm < 10 ? 2 : 1)} nm`;
}

function build(o: EblVrmOpts): FeatureCollection {
  const features: Feature[] = [];
  if (o.centre) {
    if (o.eblOn) {
      const end = rhumbDestination(o.centre, o.eblBearingDeg, EBL_LENGTH_NM);
      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: [o.centre, end] },
        properties: { kind: 'ebl' },
      });
      // Bearing label part-way out so it sits clear of the boat symbol.
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: rhumbDestination(o.centre, o.eblBearingDeg, 2.2) },
        properties: { kind: 'ebl-label', label: bearingLabel(o.eblBearingDeg) },
      });
    }
    if (o.piOn && o.piOffsetNm !== 0) {
      // The PI line: through the point one offset abeam of the boat, running
      // both ways along the index bearing, so it crosses the view like the
      // radar line it borrows from. It moves with the boat on purpose: steer
      // to hold the mark on the line and the offset is the passing distance.
      const side = o.piOffsetNm >= 0 ? 90 : -90;
      const origin = rhumbDestination(o.centre, o.piBearingDeg + side, Math.abs(o.piOffsetNm));
      const fwd = rhumbDestination(origin, o.piBearingDeg, PI_LENGTH_NM);
      const back = rhumbDestination(origin, o.piBearingDeg + 180, PI_LENGTH_NM);
      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: [back, origin, fwd] },
        properties: { kind: 'pi' },
      });
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: rhumbDestination(origin, o.piBearingDeg, 2.2) },
        properties: {
          kind: 'pi-label',
          label: `PI ${bearingLabel(o.piBearingDeg)} · ${Math.abs(o.piOffsetNm).toFixed(2)} nm ${o.piOffsetNm >= 0 ? 'stbd' : 'port'}`,
        },
      });
    }
    if (o.vrmOn && o.vrmRangeNm > 0) {
      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: rhumbCircle(o.centre, o.vrmRangeNm, 90) },
        properties: { kind: 'vrm' },
      });
      // Range label on the NE diagonal so it dodges the EBL if both are on.
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: rhumbDestination(o.centre, 45, o.vrmRangeNm) },
        properties: { kind: 'vrm-label', label: rangeLabel(o.vrmRangeNm) },
      });
    }
  }
  return { type: 'FeatureCollection', features };
}

const EMPTY: EblVrmOpts = { centre: null, eblOn: false, eblBearingDeg: 90, vrmOn: false, vrmRangeNm: 1, piOn: false, piBearingDeg: 45, piOffsetNm: 0.5 };

/**
 * Lift the EBL/VRM layers to the top of the stack, in draw order, so they are
 * never buried. Same reasoning as ownship/route/marks: the boat's overlays are
 * created before the async vector chart cells finish loading, and later a
 * provider switch, symbol-set restyle or palette swap re-adds the opaque cell
 * base fills ABOVE these lines, hiding them. Ownship/route/marks pop back because
 * their update path re-raises on every fix; the EBL/VRM update did not, so once
 * buried the lines stayed hidden even when toggled on. Re-seating on both the
 * ensure and the update path keeps them visible. moveLayer with no `before` moves
 * a layer to the top, so walking bottom -> top leaves the labels highest. Missing
 * layers are skipped.
 */
export function raiseEblVrmLayer(map: MaplibreMap): void {
  for (const id of EBL_VRM_LAYER_IDS) {
    if (map.getLayer(id)) map.moveLayer(id);
  }
}

export function ensureEblVrmLayer(map: MaplibreMap, palette: Palette): void {
  const p = getPalette(palette);
  // The accent (magenta) is reserved here for EBL/VRM, distinct from the grey
  // range rings, so the eye separates "the lines I'm steering by" from "the
  // scale rings".
  const accent = p.CHMGD;
  if (!map.getSource(SOURCE_ID)) {
    map.addSource(SOURCE_ID, { type: 'geojson', data: build(EMPTY) });
  }
  // VRM circle: dashed, so it reads differently from a solid range ring.
  if (!map.getLayer('ebl-vrm-vrm')) {
    map.addLayer({
      id: 'ebl-vrm-vrm',
      type: 'line',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'vrm'],
      paint: { 'line-color': accent, 'line-width': 1.8, 'line-opacity': 0.95, 'line-dasharray': [3, 2] },
    });
  }
  // PI line: dash-dot, the chartwork convention for an index line, so it never
  // reads as the EBL (solid) or the VRM (plain dash).
  if (!map.getLayer('ebl-vrm-pi')) {
    map.addLayer({
      id: 'ebl-vrm-pi',
      type: 'line',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'pi'],
      paint: { 'line-color': accent, 'line-width': 1.8, 'line-opacity': 0.95, 'line-dasharray': [6, 2, 1, 2] },
    });
  }
  // EBL bearing line: solid, slightly bolder, so it leads the eye out from the boat.
  if (!map.getLayer('ebl-vrm-ebl')) {
    map.addLayer({
      id: 'ebl-vrm-ebl',
      type: 'line',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'ebl'],
      paint: { 'line-color': accent, 'line-width': 2, 'line-opacity': 0.95 },
    });
  }
  if (!map.getLayer('ebl-vrm-label')) {
    map.addLayer({
      id: 'ebl-vrm-label',
      type: 'symbol',
      source: SOURCE_ID,
      filter: ['any', ['==', ['get', 'kind'], 'ebl-label'], ['==', ['get', 'kind'], 'vrm-label'], ['==', ['get', 'kind'], 'pi-label']],
      layout: {
        'text-field': ['get', 'label'],
        'text-font': ['Open Sans Bold'],
        'text-size': 11,
        'text-allow-overlap': true,
      },
      paint: { 'text-color': accent, 'text-halo-color': p.UI_BG, 'text-halo-width': 1.6 },
    });
  }
  // Whether we just created them or they already existed (made early, before the
  // async cells loaded), keep them above the cell fills so nothing hides behind
  // the vector chart.
  raiseEblVrmLayer(map);
}

export function updateEblVrmLayer(map: MaplibreMap, o: EblVrmOpts): void {
  const src = map.getSource(SOURCE_ID);
  if (src) (src as GeoJSONSource).setData(build(o));
  // Re-seat on every update (fires on each boat fix and every toggle change), so
  // the lines climb back above any cell/base layers re-added by a provider,
  // symbol-set or palette change since they were created — the same defence the
  // ownship layer gets on every fix.
  raiseEblVrmLayer(map);
}
